import React, { useState, useEffect, useMemo } from 'react';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import {
  getTopChartsDataBrainRankList,
  getTopChartsDataBrainTrend,
} from '@/pages/intelligence/pages/TopCharts/MobileGames/hooks/serviceHooks';
import { TOPGAMENUM } from '@/pages/intelligence/pages/TopCharts/_config/topChartsConst';
import { fetchGuideStatus } from '@/pages/intelligence/services/topcharts';
import { TableSelectionType } from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import { dbiBtnLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { typeIdObj } from '@/pages/home/utils/buriedPoint';
import { RecordItemType } from '@/pages/intelligence/components/SearchRecent/type';
import { getCountryFlag } from '@/pages/home/_config/countries';
import {
  handleTableDefaultSelectGames,
  filterGamesWithRank,
  sortTableByMetric,
} from '@/pages/intelligence/utils/tableUtils';
import {
  updateShortLinkFilterId,
  updateTimeFilterValue,
  URL_CHART_TYPE,
  ShortLinkQueryType,
  URL_FILTER_KEY,
} from '@intelligence/utils/urlPathCfg';

type PrimaryKey = 'id' | 'app_id' | 'unified_id';

type Game = {
  app_id: string;
  unified_id: string;
};

type UseTopChartsConfig = {
  // 游戏主键 id 字段
  primaryKey?: PrimaryKey;
  // 前几名，用于全选勾选时，勾选排名前几的游戏
  topChartsType: string;
  isDataBrain?: boolean;
  defaultSelectGameNum?: number;
  defaultParams: object;
  shortLinkValidQuery: ShortLinkQueryType;
  filterValue: any;
  setFilterVale: React.Dispatch<React.SetStateAction<any>>;
  // 是否有短链接
  hasUrlShortLink?: boolean;
  sortByFront?:boolean;
};

type UseTopChartsResponse<E extends Game> = {
  fetchParams: any;
  // 宽表总数据条数
  tableTotal: number;
  // 宽表数据
  tableData: E[];
  // echarts 图表类型
  chartType: ChartType;
  // echarts 图表类型 setter
  setChartType: React.Dispatch<React.SetStateAction<ChartType>>;
  // 柱状图、饼图 数据源
  barOrPieData: any[];
  setBarOrPieData: React.Dispatch<React.SetStateAction<any[]>>;
  // 折线图数据源
  lineData: any[];
  // 首次调用接口后的回调
  getGameData: Function;
  // 折线图接口后的回调
  getLineChartData: Function;
  // 宽表默认勾选的前 6 数据
  tableSelection: TableSelectionType[];
  setTableSelection: React.Dispatch<React.SetStateAction<any[]>>;
  // 宽表默认勾选的前 6 数据
  defaultSelectionGames: TableSelectionType[];
  // 是否重新操作过复选器提交
  isNewComfirm: boolean;
  // 是否重新操作过复选器提交 setter
  setIsNewComfirm: React.Dispatch<React.SetStateAction<boolean>>;
  // 是否重新勾选过游戏
  isNewCheckGame: boolean;
  // 是否重新勾选过游戏 setter
  setIsNewCheckGame: React.Dispatch<React.SetStateAction<boolean>>;
  /** 首次进入表格展示提示信息 */
  showFirstGuide: boolean;
  /** 首次展示下载弹窗提示 */
  firstDownLoadBubleShow: boolean;
  setFirstDownLoadBubleShow: React.Dispatch<React.SetStateAction<boolean>>;
  /** 游戏继承不到游戏时弹窗 */
  showNoGameExtendModal: boolean;
  setShowNoGameExtendModal: React.Dispatch<React.SetStateAction<boolean>>;
  /** 是否勾选过游戏，取消和全选为没勾选过，可用于继承和加载可视化 Lading  */
  operatedGame: boolean;
  isNodata?: boolean;
  // echarts 图表类型 setter
  setOperatedGame: React.Dispatch<React.SetStateAction<boolean>>;
  handleClearSearch: Function;
  transTableDataForSearch: Function;
  handleSortByFront?: Function;
};

/**
 * 获取前 top 的数据
 * @param list 源数据
 * @returns 前 top 的数据
 */
const handleTableToBar = <T extends Game>(list: T[], end = TOPGAMENUM): T[] => {
  if (!Array.isArray(list)) return [];
  return list.slice(0, end);
};

export default function useActiveUserModel<E extends Game>(
  config: UseTopChartsConfig,
): UseTopChartsResponse<E> {
  const {
    topChartsType,
    defaultSelectGameNum = TOPGAMENUM,
    defaultParams = {},
    shortLinkValidQuery,
    filterValue,
    setFilterVale,
    hasUrlShortLink,
    fixedSelection = [],
    sortByFront = false,
    primaryKey = 'app_id'
  } = config || {};

  // 查询表单对象
  const [fetchParams, setFetchParams] = useState({
    ...defaultParams,
  });

  // 宽表总数据条数
  const [tableTotal, setTableTotal] = useState(0);
  // 宽表数据
  const [tableData, setTableData] = useState<E[]>([]);
  // echarts 图表类型
  const [chartType, setChartType] = useState<ChartType>(
    (shortLinkValidQuery?.[URL_CHART_TYPE] as ChartType) || ChartType.Bar,
  );
  // 柱状图、饼图 数据源
  const [barOrPieData, setBarOrPieData] = useState<any[]>([]);
  // 折线图数据源
  const [lineData, setLineData] = useState<any[]>([]);
  // 宽表勾选的数据
  const [defaultSelectionGames, setDefaultSelectionGames] = useState<TableSelectionType[]>([]);
  const [tableSelection, setTableSelection] = useState<TableSelectionType[]>([]);

  // 是否重新操作过复选器提交
  const [isNewComfirm, setIsNewComfirm] = useState(true);
  // 是否重新勾选过游戏
  const [isNewCheckGame, setIsNewCheckGame] = useState(true);
  /** 真正的是否重新勾选游戏，上面那个 isNewCheckGame 需要做优化处理 */
  const [operatedGame, setOperatedGame] = useState(false);
  const [showFirstGuide, setShowFirstGuide] = useState(false);
  const [firstDownLoadBubleShow, setFirstDownLoadBubleShow] = useState(false);
  const [showNoGameExtendModal, setShowNoGameExtendModal] = useState(false);
  const [tableListForClearSearch, setTableListForClearSearch] = useState([]);
  const [totalForClearSearch, setTotalForClearSearch] = useState(0);

  useEffect(() => {
    setChartType((shortLinkValidQuery?.[URL_CHART_TYPE] as ChartType) || ChartType.Bar);
  }, [shortLinkValidQuery?.[URL_CHART_TYPE]]);

  const getFetchGuideStatus = async (params: any, isDownloads: boolean) => {
    try {
      const res = await fetchGuideStatus(params);
      if (res?.code === 0) {
        if (isDownloads) {
          setFirstDownLoadBubleShow(res?.data);
        } else {
          setShowFirstGuide(res?.data);
        }
      }
    } catch (e) {
    } finally {
    }
  };

  useEffect(() => {
    /** activeuser 页：获取首次进入表格提示游戏关注信息 */
    if (topChartsType === 'activeuser') {
      getFetchGuideStatus(
        {
          // Topchart 榜单第一个落地页表格关注提示
          button_id: dbiBtnLogMes.topchartMobileGameActiveUserTableTip.buttonId,
          type_id: typeIdObj.intelligence,
        },
        false,
      );
      getFetchGuideStatus(
        {
          // Topchart 榜单第一个落地页下载按钮提示
          button_id: dbiBtnLogMes.topchartMobileGameActiveUserDownLoadTip.buttonId,
          type_id: typeIdObj.intelligence,
        },
        true,
      );
    }
  }, [topChartsType]);

  const updateGetGameData = (
    list: Game[],
    pageParams: any,
    isCheckOrSearch: boolean,
    isUpdateLineChart = true,
  ) => {
    let checkGameList = tableSelection;
    if (pageParams.page === 1) {
      // 过滤rank==0后的游戏可勾选展示可视化
      const filterNoRankGameList = filterGamesWithRank(list);
      /** 第一页时拿到默认前6个游戏作为游戏全选值, 筛选掉被固定的值 */
      const filterSelector = filterNoRankGameList
        .slice(0, TOPGAMENUM)
        ?.filter((t) => !fixedSelection?.includes(t?.[primaryKey]));
      // 固定选中的要一直被选中，将它的信息过滤出来
      const fixedSelectionGameMes: TableSelectionType[] = filterNoRankGameList?.filter((t) =>
        fixedSelection?.includes(t?.[primaryKey]),
      );
      // 过滤掉选中中有固定选中的，再合并固定的值，取TOPGAMENUM个后按rank排序
      const defaultSelectGames = [...fixedSelectionGameMes, ...filterSelector]
        ?.slice(0, TOPGAMENUM)
        ?.sort((a, b) => a?.rank - b?.rank);
      // 将实际被选中的值做游戏选择处理添加game_id
      const defaultGames = handleTableDefaultSelectGames(defaultSelectGames, 'id');
      setDefaultSelectionGames(defaultGames);
      /** 在第一页且没有勾选游戏时默认选中前6（游戏在排序或重新筛选时有更新） */
      if (!isCheckOrSearch) {
        setTableSelection(defaultGames);
        /** 分页排序请求接口时，可视化不改变 */
        const barOrPieDataTmp = handleTableToBar(defaultGames, TOPGAMENUM);
        setBarOrPieData([...barOrPieDataTmp]);
        checkGameList = defaultGames;
      }
    }
    if (chartType === ChartType.Line && isUpdateLineChart) {
      if (checkGameList.length > 0) {
        const idList = checkGameList.map((t) => t?.gameId);
        getLineChartData(pageParams, idList);
      } else {
        setLineData([]);
      }
    }
  };

  const getGameData = async (argums: {
    params: any;
    isCheckOrSearch: boolean;
    isUpdateLineChart: boolean;
    updateFilterValues: any;
    filterSaveName?: string;
  }) => {
    const {
      params,
      isCheckOrSearch,
      isUpdateLineChart = true,
      updateFilterValues = filterValue,
      filterSaveName,
    } = argums;
    // 当时间为空时，等时间返回再更新，否则直接写 token 到接口
    const isUpdateShortLinkNow = !!params?.start_date && !!params?.end_date;
    // 时间如果是空的，则需要更新时间筛选器
    const needUpdateTime = !params?.start_date || !params?.end_date;
    if (isUpdateShortLinkNow && hasUrlShortLink) {
      updateShortLinkFilterId({ [URL_FILTER_KEY]: updateFilterValues });
    }
    try {
      setTableData([]);
      setFetchParams(params);
      const isSearch = params?.game_code?.length > 0;
      const { total, list, req } = await getTopChartsDataBrainRankList(params);

      let checkGameList = tableSelection;

      if (params.page === 1) {
        // 过滤 rank==0 后的游戏可勾选展示可视化
        const filterNoRankGameList = filterGamesWithRank(list);
        /** 第一页时拿到默认前 6 个游戏作为游戏全选值 */
        const defaultGames = handleTableDefaultSelectGames(
          filterNoRankGameList.slice(0, defaultSelectGameNum),
          'id',
        );
        setDefaultSelectionGames(defaultGames);
        /** 在第一页且没有勾选游戏时默认选中前 6（游戏在排序或重新筛选时有更新） */
        if (!isCheckOrSearch) {
          setTableSelection(defaultGames);
          /** 分页排序请求接口时，可视化不改变 */
          const barOrPieDataTmp = handleTableToBar<E>(filterNoRankGameList, defaultSelectGameNum);
          setBarOrPieData(barOrPieDataTmp);

          checkGameList = defaultGames;
        }
      }
      if (!isSearch) {
        // 有 game_code 表示是搜索，则不存页面数据
        setTableListForClearSearch(list || []);
        setTotalForClearSearch(total || list?.length);
      }
      const { start_date: startDate, end_date: endDate } = req || {};
      const newFetchParams = {
        ...params,
        start_date: startDate || params?.start_date || '',
        end_date: endDate || params?.end_date || '',
        order: req?.order || params?.order,
        order_source: req?.order_source || params?.order_source,
        order_metric: req?.order_metric || params?.order_metric,
        ratio_type: req?.ratio_type || params?.ratio_type,
        filter_ids: isSearch ? params?.filter_ids : req?.filter_ids,
      };
      setFetchParams(newFetchParams);
  
      if(sortByFront) {
        const { tableList: sortList, params: paramsSort } = handleSortByFront({
          list,
          params: newFetchParams,
          isCheckGame: isCheckOrSearch,
          isUpdateLineChart,
        });
        // pageParams = { ...pageParams, ...paramsSort };
        setTableData(sortList);
        setTableTotal(sortList?.length);
      } else {
        setTableTotal(total || list?.length);
        setTableData(list || []);
      }

      // 获取到时间后更新筛选器的值，并更新 url 短链接
      if (needUpdateTime && startDate && endDate) {
        const newFilterValue = updateTimeFilterValue({
          // hasUrlShortLinkVal,
          haveFilterSaveBtn: true,
          filterSaveName,
          filterValue: updateFilterValues,
          timeFilterValues: {
            type: newFetchParams.date_type,
            value: [newFetchParams.start_date, newFetchParams.end_date],
          },
        });
        setFilterVale(newFilterValue);
        // 如果是时间为空等时间返回更新 且有短链接功能，则更新短链接
        if (!isUpdateShortLinkNow && hasUrlShortLink) {
          updateShortLinkFilterId({ [URL_FILTER_KEY]: newFilterValue });
        }
      }
      // 注释掉这个无数据判断，搜索的情况不能清空；并且 list 无数据已经处理
      // if (list.length == 0) {
      //   setBarOrPieData([]);
      //   setLineData([]);
      //   setTableSelection([]);
      // } else {
      if (chartType === ChartType.Line && isUpdateLineChart) {
        if (checkGameList.length > 0) {
          const idList = checkGameList.map((t) => t?.gameId);
          getLineChartData(params, idList);
        }
      }
      // }
    } catch (e) {
      console.error(e);
      setTableData([]);
      setBarOrPieData([]);
      setLineData([]);
      setTableSelection([]);
    } finally {
    }
  };

  const getLineChartData = async (params: any, idList: string[]) => {
    if (idList?.length > 0) {
      try {
        const newFetchParams = {
          ...params,
          game_code: idList,
        };
        setLineData([]);
        const { list, barList } = await getTopChartsDataBrainTrend({ ...newFetchParams });
        setLineData(list);
        setIsNewComfirm(false);
        setIsNewCheckGame(false);

        if (params.chart_type === 1) {
          /** 标签切换游戏继承 */
          setTableSelection(handleTableDefaultSelectGames(barList, 'id'));
          setBarOrPieData(barList);
          if (barList.length === 0) {
            /** 没有继承到游戏弹窗 */
            setShowNoGameExtendModal(true);
            return;
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
      }
    } else {
      // 未勾选任何游戏，不请求，返回为空数据
      setLineData([]);
    }
  };

  const isNodata = useMemo(() => {
    if (tableTotal < 1 || tableSelection.length < 1) {
      return true;
    }
    return false;
  }, [tableTotal, tableSelection]);

  const handleClearSearch = () => {
    setFetchParams({ ...fetchParams, game_code: [] });
    setTableData(tableListForClearSearch);
    setTableTotal(totalForClearSearch);
  };

  const transTableDataForSearch = (res: any) => {
    const resultList = res?.list;
    if (!Array.isArray(resultList) || !resultList?.length) {
      return [];
    }
    const tranksList: RecordItemType[] = resultList.map((item) => ({
      id: item?.id,
      imgSrc: item?.cover,
      lineOneStr: item?.name,
      lineTwoImg: getCountryFlag(item?.publisher_addr),
      lineTwoImgName: item?.publisher_addr,
      // imageType: '',
      lineTwoStr: item?.publisher,
    }));

    return tranksList;
  };


  const handleSortByFront = (args: {
    params: any;
    isCheckGame: boolean;
    isUpdateLineChart: boolean;
    list: Game[];
  }) => {
    const { list = tableData, params, isCheckGame, isUpdateLineChart = true } = args;

    setFetchParams(params);
    const sortParams = {
      ratioType: params?.ratio_type,
      sortVal: params?.order,
      sortMetric: params?.order_metric,
      sortSource: params?.order_source,
    };

    const getMetacriticValueFun = (cvm: Game) => {
      const metricsList = Array.isArray(cvm?.metric_list) ? cvm?.metric_list : [];
      const metricHistory = metricsList.find(
        (item) => item?.metric === 'metacritic' && item?.title === 'meta_score',
      );
      return metricHistory?.history?.find((item) => item?.device === params?.order_device);
    };
    const isSortMetric =
      sortParams.sortMetric === 'meta_score' && params.order_source === 'metacritic';
    const sortTable = sortTableByMetric({
      tableData: list,
      sortParams,
      growthProperty: 'growth_ratio',
      listProperty: 'metric_list',
      metricProperty: 'metric',
      sourceProperty: 'source',
      // 评分的排序比较特殊
      getMetricValueFun: isSortMetric ? getMetacriticValueFun : undefined,
    });
    // 建议没有排名的放在最后
    const tableUpdateRank = sortTable?.map((t, idx) => ({ ...t, rank: idx + 1 }));
    updateGetGameData(tableUpdateRank, params, isCheckGame, isUpdateLineChart);
    setTableData(tableUpdateRank);
    // 前端排序后的Id的回填到参数用于有序下载
    const newFetchParams = {
      ...params,
      filter_ids: tableUpdateRank?.map((t) => t?.[primaryKey]),
    };
    setFetchParams(newFetchParams);
    return { tableList: tableUpdateRank, params: newFetchParams };
  };
  return {
    showFirstGuide,
    fetchParams,
    tableTotal,
    tableData,
    chartType,
    setChartType,
    barOrPieData,
    lineData,
    getGameData,
    getLineChartData,
    defaultSelectionGames,
    isNewComfirm,
    setIsNewComfirm,
    isNewCheckGame,
    setIsNewCheckGame,
    tableSelection,
    setBarOrPieData,
    firstDownLoadBubleShow,
    setFirstDownLoadBubleShow,
    operatedGame,
    setOperatedGame,
    setTableSelection,
    isNodata,
    showNoGameExtendModal,
    setShowNoGameExtendModal,
    handleClearSearch,
    transTableDataForSearch,
    handleSortByFront,
  };
}
